/**
 * Returned calls rate — unique numbers that called back per 1,000 outbound dials.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { buildOutboundAnalyticsParams, type WidgetFilterConfig } from '../../lib/widgetScope';
import { formatAnalyticsCount, formatCallbacksPer1000 } from '../../lib/outboundAnalyticsDisplay';

type ApiResponse = {
  range: string;
  date_from: string;
  date_to: string;
  outbound_dials: number;
  unique_callbacks: number;
  callbacks_per_1000: number;
  callback_via_inbound: number;
  callback_via_abandon: number;
};

interface Props {
  config?: WidgetFilterConfig;
}

const REFRESH_MS = 60_000;

export default function WidgetOutboundCallbackRate({ config }: Props) {
  const { getToken } = useAuth();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const params = buildOutboundAnalyticsParams(config);
    try {
      const res = await fetch(`${base}/api/analytics/outbound-callbacks?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        setError('Admin access required');
        setData(null);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError((body as { error?: string }).error || `HTTP ${res.status}`);
        setData(null);
        return;
      }
      setData((await res.json()) as ApiResponse);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [getToken, config]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchData().then(() => {
      if (cancelled) return;
    });
    const id = window.setInterval(() => void fetchData(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [fetchData]);

  const hasData = (data?.outbound_dials ?? 0) > 0;

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0.5rem',
        textAlign: 'center',
        containerType: 'size',
      }}
    >
      {loading && !data && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</p>
      )}
      {error && <p style={{ color: 'var(--error)', fontSize: '0.875rem' }}>{error}</p>}
      {!loading && !error && !hasData && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.45 }}>
          No outbound data for this period yet.
        </p>
      )}
      {!error && hasData && data && (
        <>
          <div style={{ fontSize: 'clamp(1.75rem, 8cqi, 2.75rem)', fontWeight: 700, color: 'var(--text)' }}>
            {formatCallbacksPer1000(data.callbacks_per_1000)}
          </div>
          <div style={{ fontSize: 'clamp(0.8rem, 3cqi, 1rem)', color: 'var(--text-muted)', marginTop: '0.35rem' }}>
            returned calls per 1,000 outbound dials
          </div>
          <p
            style={{
              margin: '0.75rem 0 0',
              fontSize: '0.75rem',
              color: 'var(--text-muted)',
              lineHeight: 1.45,
              maxWidth: '28rem',
            }}
          >
            {formatAnalyticsCount(data.unique_callbacks)} unique numbers returned ·{' '}
            {formatAnalyticsCount(data.outbound_dials)} outbound dials
          </p>
          {(data.callback_via_abandon > 0 || data.callback_via_inbound > 0) && (
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              {formatAnalyticsCount(data.callback_via_inbound)} via inbound answer ·{' '}
              {formatAnalyticsCount(data.callback_via_abandon)} via queue abandon
            </p>
          )}
        </>
      )}
    </div>
  );
}
